import { httpClient } from "@/shared/api";

import type {
  Booking,
  CancelBookingInput,
  CreateBookingInput,
  DaySummary,
  JournalDay,
  RescheduleBookingInput,
} from "../model/types";
import type {
  AvailabilityResponseDTO,
  BookingResponseDTO,
  CancelBookingRequestDTO,
  CreateBookingRequestDTO,
  JournalDayResponseDTO,
  ListBookingsResponseDTO,
  RescheduleBookingRequestDTO,
  SummaryResponseDTO,
} from "./dto";

function toBooking(dto: BookingResponseDTO): Booking {
  return {
    id: dto.id,
    companyId: dto.company_id,
    masterId: dto.master_id,
    masterServiceId: dto.master_service_id,
    serviceId: dto.service_id,
    clientId: dto.client_id,
    clientName: dto.client_name,
    clientPhone: dto.client_phone,
    startsAt: dto.starts_at,
    endsAt: dto.ends_at,
    bufferMinutes: dto.buffer_minutes,
    priceCents: dto.price_cents,
    status: dto.status,
    version: dto.version,
    cancelReason: dto.cancel_reason,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  };
}

function toJournalDay(dto: JournalDayResponseDTO): JournalDay {
  return {
    date: dto.date,
    masters: dto.masters.map((m) => ({
      id: m.id,
      name: m.name,
      intervals: m.working_intervals.map((i) => ({
        start: i.start,
        end: i.end,
      })),
      serviceIds: m.service_ids ?? [],
    })),
    totalMasterCount: dto.total_master_count,
    grid: dto.grid ? { start: dto.grid.start, end: dto.grid.end } : null,
  };
}

/**
 * `GET /api/bookings?date=<date>` — всі записи дня. Приймає `signal` для
 * скасування при швидкому day-switch (caller передає зі стору T2).
 */
export async function listBookingsForDay(
  date: string,
  signal?: AbortSignal,
): Promise<Booking[]> {
  const { data } = await httpClient.get<ListBookingsResponseDTO>("/bookings", {
    params: { date },
    signal,
  });
  return data.bookings.map(toBooking);
}

/**
 * `GET /api/journal/day?date=<date>` — структура дня (майстри, grid).
 * Приймає `signal` для скасування при day-switch.
 */
export async function getJournalDay(
  date: string,
  signal?: AbortSignal,
): Promise<JournalDay> {
  const { data } = await httpClient.get<JournalDayResponseDTO>("/journal/day", {
    params: { date },
    signal,
  });
  return toJournalDay(data);
}

/**
 * `GET /api/availability?master_id=&service_id=&date=` — доступні слоти.
 * Повертає масив UTC-рядків (RFC3339).
 */
export async function getAvailability(
  masterId: string,
  serviceId: string,
  date: string,
): Promise<string[]> {
  const { data } = await httpClient.get<AvailabilityResponseDTO>(
    "/availability",
    {
      params: { master_id: masterId, service_id: serviceId, date },
    },
  );
  return data.slots;
}

/** `GET /api/summary?period=day&date=<date>` — підсумок дня. */
export async function getDaySummary(date: string): Promise<DaySummary> {
  const { data } = await httpClient.get<SummaryResponseDTO>("/summary", {
    params: { period: "day", date },
  });
  return {
    completedCount: data.completed_count,
    revenueCents: data.revenue_cents,
  };
}

/** `POST /api/bookings` (CSRF) — створити запис; 201 → Booking. */
export async function createBooking(input: CreateBookingInput): Promise<Booking> {
  const body: CreateBookingRequestDTO = {
    master_id: input.masterId,
    service_id: input.serviceId,
    client_name: input.clientName,
    client_phone: input.clientPhone,
    starts_at: input.startsAt,
    ...(input.clientId ? { client_id: input.clientId } : {}),
  };
  const { data } = await httpClient.post<BookingResponseDTO>("/bookings", body);
  return toBooking(data);
}

/** `PATCH /api/bookings/{id}` (CSRF) — перенести запис; 200 → Booking. */
export async function rescheduleBooking(
  id: string,
  input: RescheduleBookingInput,
): Promise<Booking> {
  const body: RescheduleBookingRequestDTO = {
    version: input.version,
    new_master_id: input.newMasterId,
    new_starts_at: input.newStartsAt,
  };
  const { data } = await httpClient.patch<BookingResponseDTO>(
    `/bookings/${id}`,
    body,
  );
  return toBooking(data);
}

/** `POST /api/bookings/{id}/cancel` (CSRF) — скасувати запис; 200 → Booking. */
export async function cancelBooking(
  id: string,
  input: CancelBookingInput,
): Promise<Booking> {
  const body: CancelBookingRequestDTO = {
    version: input.version,
    reason: input.reason,
  };
  const { data } = await httpClient.post<BookingResponseDTO>(
    `/bookings/${id}/cancel`,
    body,
  );
  return toBooking(data);
}
